import { Suspense, lazy, useEffect, useRef, type ComponentType } from 'react'
import { BrowserRouter, Route, Routes, Navigate, useParams } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { MsalProvider } from '@azure/msal-react'
import { type IPublicClientApplication } from '@azure/msal-browser'
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { AuthGuard } from '@/components/AuthGuard'
import { SessionExpiryModal } from '@/components/SessionExpiryModal'
import { AppLayout } from '@/components/layout/AppLayout'
import { MantasticLayout } from '@/components/layout/MantasticLayout'
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary'
import { Skeleton } from '@/components/ui/skeleton'
import { Toaster } from '@/components/ui/sonner'
import CampaignReportingRedirect from '@/components/hubs/CampaignReportingRedirect'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      retry: 1,
    },
  },
})

function PrivateQueryBoundary() {
  const { user } = useAuth()
  const client = useQueryClient()
  const previous = useRef<string | null>(null)
  useEffect(() => {
    if (previous.current && previous.current !== (user?.userId ?? null)) client.clear()
    previous.current = user?.userId ?? null
  }, [user?.userId, client])
  return null
}

function PageLoader() {
  return (
    <div className="space-y-4 p-6">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  )
}

// Wrap a lazy component in Suspense + RouteErrorBoundary + Sentry profiler
function withRouteShell<P extends object>(Component: ComponentType<P>) {
  const Profiled = Sentry.withProfiler(Component)
  return function RouteShell(props: P) {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Profiled {...props} />
        </Suspense>
      </RouteErrorBoundary>
    )
  }
}

// Unauthenticated routes
const Login = withRouteShell(lazy(() => import('@/pages/Login')))
const AuthCallback = withRouteShell(lazy(() => import('@/pages/AuthCallback')))

// Public booking — the Calendly replacement. These render for people who have never
// heard of FRIDAY, so they sit OUTSIDE AuthGuard and AppLayout: no sidebar, no
// header, no session required. They carry their own chrome.

// Protected routes — each code-split, each has error boundary
const Today = withRouteShell(lazy(() => import('@/pages/Today')))
const ContactDetail = withRouteShell(lazy(() => import('@/pages/ContactDetail')))
const OpportunityDetail = withRouteShell(lazy(() => import('@/pages/OpportunityDetail')))

// Legacy → unified redirects: former pursuit/engagement detail URLs now point at the opportunity.
function RedirectToOpportunity() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/opportunities/${id}`} replace />
}
const Tasks = withRouteShell(lazy(() => import('@/pages/Tasks')))
// FRIDAY operational pages
const CampaignDetail = withRouteShell(lazy(() => import('@/pages/CampaignDetail')))
const Memories = withRouteShell(lazy(() => import('@/pages/MemoriesPage')))
const PartnerDetail = withRouteShell(lazy(() => import('@/pages/PartnerDetail')))
const TalentDetail = withRouteShell(lazy(() => import('@/pages/TalentDetail')))
const SettingsPage = withRouteShell(lazy(() => import('@/pages/SettingsPage')))
const Reference = withRouteShell(lazy(() => import('@/pages/ReferencePage')))
const Brand = withRouteShell(lazy(() => import('@/pages/BrandPage')))
const Delivery = withRouteShell(lazy(() => import('@/pages/DeliveryPage')))
const DeliveryProject = withRouteShell(lazy(() => import('@/pages/DeliveryProjectPage')))
const Timesheet = withRouteShell(lazy(() => import('@/pages/TimesheetPage')))
const IdeasBoard = withRouteShell(lazy(() => import('@/pages/IdeasBoard')))
const LeadMagnets = withRouteShell(lazy(() => import('@/pages/LeadMagnetsPage')))
const ClubDetail = withRouteShell(lazy(() => import('@/pages/ClubDetail')))
const Authority = withRouteShell(lazy(() => import('@/pages/AuthorityPage')))

// Consolidated hub surfaces — several former top-level pages folded into one
// tabbed page each (nav consolidation phase 1).
const PipelineHub = withRouteShell(lazy(() => import('@/components/hubs/PipelineHub')))
const ProposalsHub = withRouteShell(lazy(() => import('@/components/hubs/ProposalsHub')))
const FinanceHub = withRouteShell(lazy(() => import('@/components/hubs/FinanceHub')))
const ContractsHub = withRouteShell(lazy(() => import('@/components/hubs/ContractsHub')))
const CalendarHub = withRouteShell(lazy(() => import('@/components/hubs/CalendarHub')))
const CampaignsHub = withRouteShell(lazy(() => import('@/components/hubs/CampaignsHub')))
const ContactsHub = withRouteShell(lazy(() => import('@/components/hubs/ContactsHub')))
const ReportsHub = withRouteShell(lazy(() => import('@/components/hubs/ReportsHub')))
const ContentHub = withRouteShell(lazy(() => import('@/components/hubs/ContentHub')))
const AcademyHub = withRouteShell(lazy(() => import('@/components/hubs/AcademyHub')))
const DraftCanvasPage = withRouteShell(lazy(() => import('@/pages/DraftCanvasPage')))
const InboxHub = withRouteShell(lazy(() => import('@/components/hubs/InboxHub')))

// Mantastic — Todd's personal CEO operating system, collapsed to one tabbed hub.
const Mantastic = withRouteShell(lazy(() => import('@/components/hubs/MantasticHub')))
const CeoWeeklyPlanning = withRouteShell(lazy(() => import('@/pages/CeoWeeklyPlanning')))
const NotFound = withRouteShell(lazy(() => import('@/pages/NotFound')))

interface AppProps {
  msalInstance: IPublicClientApplication
}

export default function App({ msalInstance }: AppProps) {
  return (
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <PrivateQueryBoundary />
            <SessionExpiryModal />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />

              {/* All protected routes share the app layout */}
              <Route element={<AuthGuard />}>
                <Route element={<AppLayout />}>
                  {/* Today is the home surface; Dashboard is the business-state view. */}
                  <Route path="/" element={<Navigate to="/today" replace />} />
                  <Route path="/today" element={<Today />} />
                  <Route path="/inbox" element={<InboxHub />} />
                  <Route path="/authority" element={<Authority />} />
                  <Route path="/contacts" element={<ContactsHub />} />
                  <Route path="/contacts/:id" element={<ContactDetail />} />
                  <Route path="/pipeline" element={<PipelineHub />} />
                  <Route path="/opportunities/:id" element={<OpportunityDetail />} />
                  {/* Legacy redirects → unified pipeline / opportunity */}
                  <Route path="/pursuits" element={<Navigate to="/pipeline" replace />} />
                  <Route path="/pursuits/:id" element={<RedirectToOpportunity />} />
                  <Route path="/engagements/:id" element={<RedirectToOpportunity />} />
                  <Route path="/tasks" element={<Tasks />} />
                  <Route path="/ceo-weekly-planning" element={<CeoWeeklyPlanning />} />
                  <Route path="/calendar" element={<CalendarHub />} />
                  <Route path="/reports" element={<ReportsHub />} />
                  <Route path="/partners/:id" element={<PartnerDetail />} />
                  <Route path="/talent/:id" element={<TalentDetail />} />
                  <Route path="/clubs/:id" element={<ClubDetail />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/proposals" element={<ProposalsHub />} />
                  <Route path="/campaigns" element={<CampaignsHub />} />
                  <Route path="/campaigns/:id" element={<CampaignDetail />} />
                  <Route path="/contracts" element={<ContractsHub />} />
                  <Route path="/memories" element={<Memories />} />
                  <Route path="/reference" element={<Reference />} />
                  <Route path="/brand" element={<Brand />} />
                  <Route path="/content" element={<ContentHub />} />
                <Route path="/academy" element={<AcademyHub />} />
                  {/* Long-form copy chat writes. Its own route so leaving and coming back is
                      just navigation, and the URL opens on any of the author's devices. */}
                  <Route path="/draft/:draftId" element={<DraftCanvasPage />} />
                  <Route path="/finance" element={<FinanceHub />} />
                  <Route path="/delivery" element={<Delivery />} />
                  <Route path="/delivery/:id" element={<DeliveryProject />} />
                  <Route path="/timesheet" element={<Timesheet />} />
                  <Route path="/ideas" element={<IdeasBoard />} />
                  <Route path="/lead-magnets" element={<LeadMagnets />} />

                  {/* Nav consolidation phase 1 — former top-level pages now live as
                      tabs on a hub. Old paths permanently redirect to their tab so
                      bookmarks, emails and assistant deep-links keep working. */}
                  <Route path="/winloss" element={<Navigate to="/pipeline?tab=winloss" replace />} />
                  <Route path="/rate-cards" element={<Navigate to="/proposals?tab=rate-cards" replace />} />
                  <Route path="/invoices" element={<Navigate to="/finance?tab=invoices" replace />} />
                  <Route path="/loa" element={<Navigate to="/contracts?tab=authorities" replace />} />
                  <Route path="/insurance" element={<Navigate to="/settings?tab=credentials" replace />} />
                  <Route path="/credentials" element={<Navigate to="/settings?tab=credentials" replace />} />
                  <Route path="/meetings" element={<Navigate to="/calendar?tab=meetings" replace />} />
                  <Route path="/clubs" element={<Navigate to="/contacts?tab=clubs" replace />} />
                  <Route path="/mailing-lists" element={<Navigate to="/contacts?tab=mailing-lists" replace />} />
                  <Route path="/introductions" element={<Navigate to="/contacts?tab=introductions" replace />} />
                  <Route path="/partners" element={<Navigate to="/contacts?tab=partners" replace />} />
                  <Route path="/talent" element={<Navigate to="/contacts?tab=talent" replace />} />
                  <Route path="/gtm" element={<Navigate to="/campaigns?tab=gtm" replace />} />
                  <Route path="/campaigns/reporting" element={<CampaignReportingRedirect />} />
                  <Route path="/workflows" element={<Navigate to="/settings?tab=workflows" replace />} />

                  {/* Nav consolidation phase 2 — Money & Content hubs */}
                  <Route path="/dashboard" element={<Navigate to="/reports?tab=overview" replace />} />
                  <Route path="/glidepath" element={<Navigate to="/reports?tab=glidepath" replace />} />
                  <Route path="/content-studio" element={<Navigate to="/content?tab=studio" replace />} />
                  <Route path="/content-register" element={<Navigate to="/content?tab=register" replace />} />
                  <Route path="/assessments" element={<Navigate to="/content?tab=assessments" replace />} />

                  {/* Nav consolidation phase 3 — Signals + watch-lists fold into Inbox */}
                  <Route path="/intake" element={<Navigate to="/inbox?tab=intake" replace />} />
                  <Route path="/alerts" element={<Navigate to="/inbox?tab=alerts" replace />} />
                  <Route path="/briefs" element={<Navigate to="/inbox?tab=briefs" replace />} />
                  <Route path="/tenders" element={<Navigate to="/inbox?tab=tenders" replace />} />
                  <Route path="/acquisitions" element={<Navigate to="/inbox?tab=acquisitions" replace />} />

                  <Route element={<MantasticLayout />}>
                    <Route path="/mantastic" element={<Mantastic />} />
                  </Route>
                  {/* Nav consolidation phase 4 — the 8 Mantastic pages are tabs now */}
                  <Route path="/mantastic/sprints" element={<Navigate to="/mantastic?tab=sprints" replace />} />
                  <Route path="/mantastic/weekly" element={<Navigate to="/ceo-weekly-planning" replace />} />
                  <Route path="/mantastic/audit" element={<Navigate to="/mantastic?tab=audit" replace />} />
                  <Route path="/mantastic/roles" element={<Navigate to="/mantastic?tab=blueprint" replace />} />
                  <Route path="/mantastic/vision" element={<Navigate to="/mantastic?tab=vision" replace />} />
                  <Route path="/mantastic/board" element={<Navigate to="/mantastic?tab=board" replace />} />
                  <Route path="/mantastic/hub" element={<Navigate to="/mantastic?tab=hub" replace />} />
                  {/* Anything unmatched inside the app shell → friendly 404 */}
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Route>
            </Routes>
          </AuthProvider>
        </BrowserRouter>
        <Toaster richColors position="bottom-right" />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </MsalProvider>
  )
}
